/** What a field's `dependsOn` resolves to against the current form. */
export type DependsOnState =
  | { kind: 'author-error'; message: string }
  | { kind: 'awaiting'; missing: readonly string[] }
  | { kind: 'ready'; values: Readonly<Record<string, unknown>> };

/** `dependsOn` as a template author may write it: one field name or a list. */
export type DependsOnOption = string | readonly string[];

/** Normalizes a template author's `dependsOn` into a plain list of field names. */
export function normalizeDependsOn(
  dependsOn: DependsOnOption | undefined,
): readonly string[] {
  if (dependsOn === undefined) return [];
  return typeof dependsOn === 'string' ? [dependsOn] : dependsOn;
}

/**
 * A form value counts as "filled" when it is neither `undefined`, `null`, an
 * empty string, an empty array, nor an empty object. Anything else (including
 * `false` and `0`) is a real value. Matches
 * `picker-toolbox/frontend/src/cascade.ts` `isFilled` exactly, so the same
 * form value reads the same way for the old and the new field family.
 *
 * The empty-object case is load-bearing: RJSF initializes an untouched
 * `type: object` field to `{}`, which is what a picker value is before the
 * user has chosen anything.
 */
export function isFilled(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

function selfDependencyError(ownName: string): string {
  return `Field "${ownName}" cannot depend on itself.`;
}

function unknownFieldError(
  unknownName: string,
  validKeys: ReadonlySet<string>,
): string {
  const knownNames = [...validKeys].sort(); // NOSONAR: sorts strings (field names); code-unit order is intended
  return `dependsOn names an unknown field "${unknownName}". Known fields: ${knownNames.join(
    ', ',
  )}.`;
}

/**
 * Resolves a field's `dependsOn` against the current form, in this order:
 * 1. a self-reference is an author error (a field cannot depend on itself);
 * 2. a reference to a field outside `validKeys` is an author error, and wins
 *    over a merely-missing value, because a typo is a bug and a missing value
 *    is normal, expected state;
 * 3. a referenced field that is not yet `isFilled` leaves the state awaiting;
 * 4. otherwise every referenced field is filled, and the state is ready with
 *    their values.
 *
 * Pure data function: same inputs -> same output, no side effects.
 */
export function resolveDependsOn(input: {
  dependsOn: readonly string[];
  formData: Readonly<Record<string, unknown>>;
  ownName: string;
  validKeys: ReadonlySet<string>;
}): DependsOnState {
  const { dependsOn, formData, ownName, validKeys } = input;

  if (dependsOn.includes(ownName)) {
    return { kind: 'author-error', message: selfDependencyError(ownName) };
  }

  const unknownName = dependsOn.find(name => !validKeys.has(name));
  if (unknownName !== undefined) {
    return {
      kind: 'author-error',
      message: unknownFieldError(unknownName, validKeys),
    };
  }

  const missing = dependsOn.filter(name => !isFilled(formData[name]));
  if (missing.length > 0) {
    return { kind: 'awaiting', missing };
  }

  const values: Record<string, unknown> = {};
  for (const name of dependsOn) {
    values[name] = formData[name];
  }
  return { kind: 'ready', values };
}
